import * as types from './types';
import Api from '../../../../../../lib/api';
import { isCustomFieldArchived } from '../template-configuration';

export const setFetchingFileDataImport = (condition) => ({
    type: types.SET_FETCHING_FILE_DATA_IMPORT,
    payload: condition,
});

export const setFileDataImportError = (error) => ({
    type: types.SET_FILE_DATA_IMPORT_ERROR,
    payload: error,
});

export const importFileData = (importType, data, holderId, callback) => (dispatch, getState) => {
    const { login: { authToken }, localization } = getState();
    const { errorDefault, errorConnection } = localization.strings.holderSettings.errors;

    dispatch(setFetchingFileDataImport(true));

    const payload = { importType, data, holderId };

    return Api.post('cf/fileDataImports', payload, authToken)
        .then(response => {
            const { success, message = '' } = response.data;
            dispatch(setFetchingFileDataImport(false));

            if (success) {
                callback(false, { success: true, message });
            } else {
                dispatch(setFileDataImportError(errorDefault));
                callback(true, { success: false, message: message || errorDefault });
            }
        })
        .catch(err => {
            const errorMsg = err.response.data.message || errorConnection;
            dispatch(setFileDataImportError(errorMsg));
            callback(true, { success: false, message: errorMsg });
        });
};

export const fetchPreviousImports = (holderId) => async (dispatch, getState) => {
  const { login: { authToken } } = getState();

  dispatch({ type: types.SET_FETCHING_PREVIOUS_IMPORTS, payload: true });

  try {
    const response = await Api.get(`cf/dataImportsDocuments?holderId=${holderId}`, authToken);

    const responseData = Array.isArray(response.data)
      ? response.data
      : Array.isArray(response.data && response.data.data)
      ? response.data.data
      : [];

    const templateTypeNames = {
      1: 'Insureds',
      2: 'Projects',
      3: 'Project & Insured Connections',
      4: 'Single-Step Project & Insured Import'
    };

    const previousImports = responseData.map(item => {
      if (!item) return item;

      // Parse ImportLog JSON if present
      let logFiles = [];
      if (item.ImportLog) {
        try {
          const logData = typeof item.ImportLog === 'string'
            ? JSON.parse(item.ImportLog)
            : item.ImportLog;
          logFiles = Array.isArray(logData) ? logData : [];
        } catch (e) {
          logFiles = [];
        }
      }

      const importTypeId = item.importTypeID != null ? item.importTypeID : item.ImportType;
      const statusValue = item.status != null ? item.status : item.Status;

      return {
        ...item,
        documentName: item.documentName || item.DocumentName || '',
        documentLink: item.documentLink || '#',
        uploadedBy: item.uploadedBy || item.UploaderName || '',
        importType: item.importType || templateTypeNames[importTypeId] || '',
        importTypeID: importTypeId || null,
        uploadDate: item.uploadDate || item.CreatedAt || '',
        recordCount: item.recordCount || 0,
        status:
          typeof statusValue === 'number'
            ? statusValue === 1
              ? 'Completed'
              : statusValue === 2
              ? 'Partially Completed'
              : statusValue === 0
              ? 'Failed'
              : String(statusValue)
            : statusValue || '',
        errorCount: item.errorCount || 0,
        processedAt: item.processedAt || item.UpdatedAt || '',
        logFiles: logFiles  // Add parsed log files
      };
    });

    dispatch({ type: types.SET_PREVIOUS_IMPORTS, payload: previousImports });
  } catch (err) {
    dispatch({
      type: types.SET_PREVIOUS_IMPORTS_ERROR,
      payload: (err.response && err.response.data && err.response.data.message) || 'Failed to load previous imports'
    });
  } finally {
    dispatch({ type: types.SET_FETCHING_PREVIOUS_IMPORTS, payload: false });
  }
};

/**
 * Fetches the active (non-archived) Project Custom Fields of a Holder.
 *
 * @param {number|string} holderId - Holder whose custom fields are needed.
 * @param {boolean} storeInRedux - When true the result also becomes the redux
 *   `dataImports.customFields`. Lookups for child holders of a parent page pass
 *   false so the page Holder's custom fields are not overwritten.
 */
export const fetchCustomFieldsForImport = (holderId, storeInRedux = true) => async (dispatch, getState) => {
  const { login: { authToken } } = getState();
  if (storeInRedux) {
    dispatch({ type: types.SET_FETCHING_CUSTOM_FIELDS, payload: true });
  }

  let customFields = [];

  try {
    const response = await Api.get(`cf/customFields?holderId=${holderId}&archived=0`, authToken);
    const responseData = response.data || {};

    if (Array.isArray(responseData)) {
      customFields = responseData;
    } else if (Array.isArray(responseData.customFields)) {
      customFields = responseData.customFields;
    } else if (Array.isArray(responseData.data)) {
      customFields = responseData.data;
    }

    // Keep only active custom fields; archived fields must never reach a template.
    customFields = customFields.filter(cf => cf && !isCustomFieldArchived(cf));

    if (storeInRedux) {
      dispatch({ type: types.SET_CUSTOM_FIELDS_FOR_IMPORT, payload: customFields });
    }
  } catch (err) {
    if (storeInRedux) {
      dispatch({
        type: types.SET_CUSTOM_FIELDS_FOR_IMPORT_ERROR,
        payload: (err.response && err.response.data && err.response.data.message) || 'Failed to load custom fields'
      });
    }
  } finally {
    if (storeInRedux) {
      dispatch({ type: types.SET_FETCHING_CUSTOM_FIELDS, payload: false });
    }
  }

  return customFields;
};